import Header from "@/layouts/carbon/carbon-header";
import { CarbonSidebar } from "./carbon-sidebar";
import { useTopBarHeight } from "@/app/shared/agent-dashboard-top-bar/top-bar-height-context";
import { BottomNavBar } from "./bottom-nav-bar";
import { useEffect, useState } from "react";

const APP_MIGRATION_BANNER_DISMISSED_KEY =
  "mobile_app_migration_banner_dismissed";
const MOBILE_APP_DOWNLOAD_URL =
  import.meta.env.VITE_MOBILE_APP_DOWNLOAD_URL || "https://onelink.to/p8vrs5";

const getStandaloneMode = () =>
  window.matchMedia("(display-mode: standalone)").matches ||
  (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

function MobileAppMigrationBanner() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (
      !getStandaloneMode() ||
      sessionStorage.getItem(APP_MIGRATION_BANNER_DISMISSED_KEY) === "true"
    ) {
      return;
    }

    setIsVisible(true);
  }, []);

  const dismiss = () => {
    sessionStorage.setItem(APP_MIGRATION_BANNER_DISMISSED_KEY, "true");
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <aside className="relative flex flex-row items-center justify-between gap-2 bg-black px-3 py-2 text-white shadow-sm md:gap-4 md:px-6">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold md:text-base">
          Switch to Our Mobile App
        </p>
        <p className="text-xs text-gray-200 md:text-sm">
          Enjoy a smoother experience. Download our official app today!
        </p>
      </div>
      <div className="flex shrink-0 items-center justify-end gap-1.5 md:gap-2">
        <a
          href={MOBILE_APP_DOWNLOAD_URL}
          target="_blank"
          rel="noreferrer"
          className="whitespace-nowrap rounded-md bg-white px-3 py-2 text-xs font-semibold text-black hover:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black md:text-sm"
        >
          Download Now
        </a>
        <button
          type="button"
          aria-label="Dismiss mobile app message"
          onClick={dismiss}
          className="rounded p-1 text-lg leading-none text-gray-200 hover:bg-gray-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black"
        >
          &times;
        </button>
      </div>
    </aside>
  );
}

export default function CarbonLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { topBarHeight } = useTopBarHeight();
  return (
    <main className="flex min-h-screen flex-grow">
      <CarbonSidebar className="fixed hidden flex-col justify-between dark:bg-gray-50 xl:block" />
      <div className="flex w-full flex-col xl:ms-[292px] xl:w-[calc(100%-292px)] 2xl:ms-[292px] 2xl:w-[calc(100%-292px)]">
        {topBarHeight > 0 && (
          <div style={{ height: `${topBarHeight}px` }}></div>
        )}
        <Header />
        <MobileAppMigrationBanner />
        <div className="flex flex-grow flex-col px-4 pb-6 pt-2 md:px-5 lg:px-6 lg:pb-8 3xl:px-8 3xl:pt-4 4xl:px-10 4xl:pb-9">
          {children}
        </div>
        <BottomNavBar />
      </div>
    </main>
  );
}
